/*
This is the core "data model" for query/analyses in DA's UI. A query/analysis consists of two parts:
- The question itself encodes all the selected PICO attributes. The question is stored the URL itself,
so generally modifications to the selected attributes are considered "navigations" (entry in history,
can go back and forward). parseQuestion handles parsing questions, and there is also significant logic
in this module for generating or modifying questions when attributes are (de)selected/grouped.
The question also has two states, the "live" state that represents the currently parsed question in
the question bar, and current state, which is updated to the live state when you click search or press
enter.
- All of the selections and settings are stored in the AnalysisSelection entities (modeled below).
The id of the AnalysisSelection entity is stored within the URL, but modifications to selections/settings
are not considered navigations, but changes to the AnalysisSelection. Modifications are
persisted (with updateAnalysis) and may be done individually or as part of a analysis/meta
request to do a meta analysis.

This module is also responsible for handling some of the URL interactions, updating and responding to
changes in query parameters (along with the modules that using app/navigation).
It also handles generating the DS and DA queries (as query parameters) for the main requests for studies
(/portal/studies and portal/articles -> /articles/term-search)
*/
import { all, B, reactive, Variable, VArray, VMap, VNumber } from 'alkali'
import { get, post, put } from 'tools/request'
import { cloneDeep, intersection } from 'lodash'
import { SelectionsModificationDialog } from 'components/navbar/SelectionsModificationDialog'
import { questionToURIComponent, questionToText, generateNewQuestion, encodeBluntLabel, decodeLabel, dsEscapeText } from 'analysis/Question'
import { AFRICAN_SENSE_ID, ASIAN_SENSE_ID, CHILD_SENSE_ID, ADOLESCENCE_SENSE_ID, ADULT_SENSE_ID, ELDERLY_SENSE_ID, FEMALE_SENSE_ID, MALE_SENSE_ID, NATIVE_AMERICAN_SENSE_ID, PACIFIC_ISLANDER_SENSE_ID, WHITE_SENSE_ID, MIXED_RACE_SENSE_ID, YOUNG_SENSE_ID, YOUTH_SENSE_ID, INFANT_SENSE_ID } from '../../portal/util/characteristics'
import app from 'app/app'
import { Cache } from 'tools/Index'

const selectionsModificationDialog = new SelectionsModificationDialog()
export const fixCasing = term => term.split(' ').map(word => word && (word[0].toUpperCase() + word.slice(1))).join(' ')

export const liveQuestion = reactive({
	text: '',
	position: 0,
})

export const liveParsed = reactive({
	annotations: [''],
	selectionsId: 0,
	interventionalScore: 0.5,
})

export const suggestions = reactive([])

export function parseQuestion(question, position?) {
	
	return get('/portal/parse-query?' + (app.user.Settings?.NaturalQuestionParsing ? 'natural=true&' : '') + 'text=' + questionToURIComponent(question) +
			(position > 0 ? '&position=' + position : '') + ('&rawText=' + encodeURIComponent(questionToText(question))))
}
let analysisNeedsSaving
class AnalysisSelections extends Variable.with({
	owner: 0,
	isOutcomePositive: {},
	selectedStudies: [],
	studySelections: {},
	analysisType: 'auto',
	settings: {
		networkMethod: '',
		effects: '',
		measureBin: 'RR',
		measureCont: 'MD',
		measureRate: 'IRR',
		useSMD: false,
		sortBy: 'pscore',
		confidenceLevel: 0,
		continuityCorrection: 0,
		reverseDirection: false,
		multiGroupPooling: true,
		additiveInterventions: false,
		hideDisconnectedGroups: false,
		autoSelectMode: 'none',
		autoSelectThreshold: 20,
		autoNMAType: 'frequentist',
		singleGroupSelection: false,
		weightedAutoSelection: false,
		seed: 0,
	},
	studyFilter: {
		published: 0,
		designs: [],
		locations: [],
		sources: [],
		importCollection: '',
		phases: {
			phase2: false,
			phase3: false,
			phase4: false,
		},
	},
	demographics: {
		age: null,
		gender: null,
		race: null,
	},
	sorting: {
		recency: 5,
		participants: 0,
		analyzable: 50,
		design: 50,
	},
	favorite: false,
	locked: false,
	regression: '',
	adjustments: [{
		treatment: '',
		predictor: '',
		outcome: '',
		m: 0,
		mean: 0,
	}],
	baseline: '',
	flaggedArticles: {},
	question: [],
	dsQuery: {
		query: '',
		name: '',
	},
	outcome: {
		unit: '',
		settingsFor: '',
	},
	analyzeAs: [{
		id: '',
		label: '',
	}],
	screeningId: '',
	name: '',
	referenceUrl: '',
	selectedStudyCount: 0,
	tags: [],
}) {
	constructor(id, selections) {
		selections.id = id
		super(selections)
		this.id = reactive(id) // stored separately so it can be accessed without waiting for promise
	}
	put(analysisSelections, event?) {
		let groupCount = groupInterventions(structuredQuery.interventions.valueOf() || []).length
		let updatedInterventions
		if (!analysisSelections.analysisType)
			analysisSelections.analysisType = 'auto'
		else if (analysisSelections.analysisType == 'direct' && this.valueOf()?.analysisType != 'direct') {
			if (groupCount <= 1) {
				if (groupCount == 1 && this.valueOf()?.analysisType == 'cohort') {
					// just trigger a rewrite of the question
					updatedInterventions = structuredQuery.interventions.valueOf()
				}
			} else if (groupCount == 2) {
				// just trigger a rewrite of the question
				updatedInterventions = structuredQuery.interventions.valueOf()
			} else {
				if (confirm('Switching to direct meta analysis will remove extra treatments in your query so there are only two, searching for head-to-head studies, do you wish to proceed?'))
					updatedInterventions = structuredQuery.interventions.slice(0, 2)
				else
					return
			}
		} else if (analysisSelections.analysisType == 'direct' && this.valueOf()?.analysisType == 'direct' && groupCount > 2) {
			if (confirm('You have selected more than two intervention groups, and direct analysis only supports the comparison of two, do you want to switch to automatic analysis so network analysis can be performed?'))
				analysisSelections.analysisType = 'auto'
			else
				return
		} else if (analysisSelections.analysisType == 'cohort' && this.valueOf()?.analysisType != 'cohort' && groupCount > 1) {
			if (confirm('Switching to cohort analysis will remove extra treatments in your query so there is only one, do you wish to proceed?'))
				updatedInterventions = structuredQuery.interventions.slice(0, 1)
			else
				return
		} else if (analysisSelections.analysisType == 'cohort' && this.valueOf()?.analysisType != 'cohort' && groupCount == 1) {
			// just trigger a rewrite of the question
			updatedInterventions = structuredQuery.interventions.valueOf()
		} else if (isNMA(analysisSelections) && !isNMA(this.valueOf())) {
			// just trigger a rewrite of the question
			updatedInterventions = structuredQuery.interventions.valueOf()
		}

		function isNMA(analysisSelections) {
			return analysisSelections?.analysisType == 'auto' || analysisSelections?.analysisType == 'bayesian' || analysisSelections?.analysisType == 'frequentist'
		}
		if (analysisSelections.analysisType == 'bayesian' && analysisSelections.settings?.measureBin == 'RD')
			analysisSelections.settings.measureBin = 'RR'
		super.put(analysisSelections, event)
		if (updatedInterventions)
			structuredQuery.interventions.put(updatedInterventions)
		if (event?.key == 'analysisType')
			analysisNeedsSaving = true
		if (event?.key == 'favorite' || event?.key == 'locked')
			return // save this separately, not a change to selections
		Promise.resolve(app.user).then(user => {
			if ((analysisSelections.owner == user?.Id || !analysisSelections.owner) && (event?.key == 'name' || event?.key == 'tags' || event?.key == 'referenceUrl'))
				return updateAnalysis()
			if (user && (analysisSelections.owner && analysisSelections.owner != user.Id ||
				(analysisSelections.owner &&
				    (analysisSelections.locked === undefined ? analysisSelections.favorite : analysisSelections.locked)
				    && !isLiveEditing.valueOf()))) {
				// this is not our analysis selection (or is a frozen favorite), fork and make our own
				analysisSelections.dbId = null // make sure the db id gets reset
				if (analysisSelections.owner == user?.Id) {
					selectionsModificationDialog.edit = () => {
						isLiveEditing.put(true)
						updateAnalysis()
					}
					selectionsModificationDialog.copy = () => {
						if (!analysisSelections.screeningId)
							delete analysisSelections.favorite
						analysisSelections.locked = false
						replaceSelections(makeId(), analysisSelections)
						updateAnalysis()
						updateLocationForSearch(true)
					}
					selectionsModificationDialog.show()
					return
				}
				this.favorite.put(undefined)
				this.locked.put(false)
				this.owner.put(user.Id)
				analysisSelections.owner = user.Id
				analysisSelections.favorite = undefined
				analysisSelections.locked = false
				replaceSelections(makeId(), analysisSelections, true)
				updateLocationForSearch(true)
			}
			updateAnalysis()
		})
	}
}
class CurrentAnalysisSelections extends AnalysisSelections {
	put(newSelections, event?) {
		return isNewQuestion().then(isNewQuestion => {
			if (isNewQuestion) {
				delete newSelections.example
				replaceSelections(makeId(), Object.assign(newSelections, {
					selectedStudies: [],
					studySelections: {},
					favorite: undefined,
					locked: false,
				}))
				newSelections.selectedStudies.initializing = true
			} else {
				super.put(analysisSelections, event)
			}
			if (event.key == 'question')
				updateLocationForSearch()

		})
	}
	get isWritable() {
		return true
	}
}

function getDefaultSelections(id) {
	let user = app.user
	let selections = {
		id,
		analysisType: 'auto',
		selectedStudies: [],
		studySelections: {},
		flaggedArticles: {},
		question: liveParsed.get('annotations'),
		settings: {
			networkMethod: 'bayesian',
			effects: 'random',
			measureBin: 'OR',
			measureCont: 'MD',
			measureRate: 'IRR',
			useSMD: false,
			sortBy: 'pscore',
		},
		studyFilter: {
			designs: [],
			published: 1970,
			importCollection: null,
		},
		demographics: {
			age: null,
			gender: null,
			race: null,
		},
		sorting: {
			recency: 5,
			participants: 0,
			analyzable: 50,
			design: 50,
		},
		locked: false
	}
	selections.selectedStudies.initializing = true
	assignUserSetting(selections.settings, 'multiGroupPooling', settings => settings?.MultiGroupPooling ?? true)
	assignUserSetting(selections.settings, 'singleGroupSelection', settings => settings?.SingleGroupSelection)
	assignUserSetting(selections.settings, 'autoSelectMode', settings => settings?.AutoSelectMode)
	assignUserSetting(selections.settings, 'autoSelectThreshold', settings => settings?.AutoSelectThreshold)
	assignUserSetting(selections.settings, 'autoNMAType', settings => settings?.AutoAnalysisType || 'frequentist')
	if (user?.then)
		user.then(user => {
			if (selections == analysisSelections.valueOf())
				analysisSelections.set('analysisType', user.Settings?.DefaultAnalysisType || 'auto')
		})
	else
		selections.analysisType = user?.Settings?.DefaultAnalysisType || 'auto'
	function assignUserSetting(settings, property, getUserSetting) {
		if (user?.then)
			settings[property] = user.then(user => settings[property] = getUserSetting(user?.Settings))
		else
			settings[property] = getUserSetting(user?.Settings)
	}
	return selections
}
let analysisId = makeId()
export let analysisSelections = new AnalysisSelections(analysisId, getDefaultSelections(analysisId))

function loadSelections(id) {
	replaceSelections(id, get('/portal/AnalysisSelections/' + id + '?app=' + (window.isFAQDashboard ? 'Dashboard' : 'Analytics')).then(selections => {
		if (selections.analysisType == 'ai' || !selections.analysisType)
			selections.analysisType = 'auto'
		if (selections.analysisType == 'network')
			selections.analysisType = 'bayesian'
		selections.studySelections = selections.studySelections || {}
		// Back-compatability for legacy queries without locked state
		if (selections.locked === undefined && !selections.example) {
			selections.locked = true
			Promise.resolve(app.user).then(user => {
				if (selections.owner == user.Id)
					updateAnalysis()
			})
		}
		return selections
	}, error => getDefaultSelections(id)), null)
}

export const showLiveAnalysis = reactive().to(() => !localStorage.getItem('hide-live-analysis'),
	(value) => localStorage[value ? 'removeItem' : 'setItem']('hide-live-analysis', true))

export const currentQuestion = reactive({})

let needsAnalysisUpdate
showLiveAnalysis.subscribe(() => {
	if (needsAnalysisUpdate) {
		needsAnalysisUpdate = false
		updateAnalysis(true)
	}
})
let updateTimeout, currentAnalysisRequest, needsFullAnalysis, needsSave, analysisPromise
export let analysisSaveRequest
export function updateAnalysis(retrieveOnly?, fullAnalysis?) {
	needsFullAnalysis = fullAnalysis
	if (!retrieveOnly)
		needsSave = true
	if (analysisPromise || navigating)
		return analysisPromise// queued, let it finish
	analysisPromise = new Promise(resolve => {
		Promise.resolve(whenInitialSearch).then(() => readonlyMode).then(readonlyMode => {
			updateTimeout = setTimeout(() => resolve((async () => {
				analysisPromise = null
				if ((await structuredQuery).isAsking && !analysisNeedsSaving)
					return
				if (currentAnalysisRequest?.abort)
					currentAnalysisRequest.abort()
				if (readonlyMode)
					needsSave = false

				if (needsSave && location.search.indexOf('imports=') > -1) {
					history.replaceState({}, questionToText(liveParsed.annotations.valueOf()), location.search.replace(/&imports=[^&#]*/, ''))
				}
				await analysisSelections.settings.multiGroupPooling
				let autoSelectMode = analysisSelections.settings.autoSelectMode

				if (document.querySelector('.analysis-selection.analysis-view'))
					needsFullAnalysis = true
				let doAnalysis = needsFullAnalysis || (showLiveAnalysis.valueOf() && (analysisSelections.selectedStudies?.length > 0 ||
						autoSelectMode == 'weighted' || autoSelectMode == 'select'))
				if (!doAnalysis) {
					needsAnalysisUpdate = true
					if (needsSave) {
						analysisSaveRequest = put('/portal/AnalysisSelections/' + analysisSelections.id + '?app=' + (window.isFAQDashboard ? 'Dashboard' : 'Analytics'), {
							data: analysisSelections
						})
						needsSave = false
					}
					metaResults.is(currentAnalysisRequest = { interventions: [] })
					return
				}

				currentAnalysisRequest = analysisSaveRequest = (needsSave ? post : get)('/portal/analysis/meta?' + await questionAsParameters() +
					'&app=' + (window.isFAQDashboard ? 'Dashboard' : 'Analytics') +
					(location.search.includes('debugR') ? '&debugR=true' : '') +
					(needsFullAnalysis ? '&fullAnalysis=true' : ''), needsSave ? {
					data: {
						analysisSelections
					}
				}: {}).catch(error => {
					if (error.status != 401 && error.name != 'Abort')
						console.error(error.toString())
					return { interventions: [], error }
				})
				needsSave = false
			})()), needsFullAnalysis ? 10 : 100)
		})
	})
	metaResults.put(analysisPromise.then(() => currentAnalysisRequest))
	return analysisPromise
}

export const currentAnalysisSelections = new CurrentAnalysisSelections(analysisId, analysisSelections)

export const studySelections = currentAnalysisSelections.studySelections

export const demographics = currentAnalysisSelections.demographics

export const studyFilter = currentAnalysisSelections.studyFilter

export const sorting = currentAnalysisSelections.sorting

export const locked = currentAnalysisSelections.locked

export const analysisType = currentAnalysisSelections.analysisType

export const isAnalysisOutcomePositive = currentAnalysisSelections.isOutcomePositive

export const selectedStudies = currentAnalysisSelections.selectedStudies

export const outcomeSettings = currentAnalysisSelections.outcome

export const docSearchQuery = currentAnalysisSelections.dsQuery

export const importedCollections = new VArray(get('/portal/ImportByClient'))

function replaceSelections(id, value, noUpdate) {
	isLiveEditing.put(false)
	if (value.screeningId && id != value.id) {
		post('/portal/Screening/' + value.screeningId, {
			data: {
				method: 'copy',
			}
		}).then(screening => {
			currentAnalysisSelections.screeningId.is(screening.screening_uuid)
		})
	}
	analysisSelections = new AnalysisSelections(id, value)
	if (noUpdate) {
		currentAnalysisSelections.value = analysisSelections
		currentAnalysisSelections.id.value = id
	} else {
		currentAnalysisSelections.is(analysisSelections)
		currentAnalysisSelections.id.is(id)
	}
}

export const includeFlagged = reactive(false)

export let analyzeAsSubscript = new Map()
export let analyzeTreatmentMap = new Map()
export const analyzeTreatmentAs = currentAnalysisSelections.analyzeAs.to(analyzeAs => liveStructuredQuery.to(({ interventions }) => {
	analyzeTreatmentMap = new Map()
	analyzeAsSubscript = new Map()
	for (let { id, ids, label } of [...(interventions || []), ...(analyzeAs || [])]) {
		if (!label)
			continue
		if (!ids)
			ids = [id]
		for (let id of ids)
			if (/^S[UG0-9]/.test(id) || typeof id == 'number') // exclude full-text, but include sense ids and groupings
				analyzeTreatmentMap.set(id, label)
		getInitialLetter(label)
	}
	return analyzeTreatmentMap
})).as(VMap)
export function getInitialLetter(label) {
	let initialLetter = label[0].toUpperCase()
	let subscript = analyzeAsSubscript.get(label)
	if (!subscript) {
		let maxSubscript = 0
		for (let [ name, subscript ] of analyzeAsSubscript) {
			if (name[0].toUpperCase() == initialLetter) {
				maxSubscript = Math.max(subscript, maxSubscript)
			}
		}
		analyzeAsSubscript.set(label, subscript = maxSubscript + 1)
	}
	return subscript
}
analyzeTreatmentAs.set = function(key, label) {
	// when the treatment map changes, recompute the structured/text query
	let analyzeAsArray = currentAnalysisSelections.analyzeAs.valueOf()
	let existing = analyzeAsArray?.find(analyzeAsGroup => analyzeAsGroup.id == key)
	if (existing) {
		existing.label = label
		currentAnalysisSelections.analyzeAs.updated()
		structuredQuery.interventions.add(existing)
	}
	let foundIntervention
	let interventions = structuredQuery.get('interventions')
	// remove any intervention that is now (completely) assigned to a different label
	for (let i = 0; i < interventions.length; i++) {
		let intervention = interventions[i]
		if (intervention.label == label) {
			// if it is now a label that matches the new label, add to the ids
			(intervention.ids || (intervention.ids = [intervention.id])).push(key)
			intervention.ids.sort((a, b) => (/^S[UG0-9]/.test(b) ? 1 : 0) - (/^S[UG0-9]/.test(a) ? 1 : 0)) // put full-text at the end
			foundIntervention = true
			if (intervention.fromText)
				intervention.fromText = false
		} else if ((intervention.ids || [intervention.id]).includes(key)) {
			if (intervention.ids) {
				intervention.ids.splice(intervention.ids.indexOf(key))
				if (intervention.fromText)
					intervention.fromText = false
			}
			if (/^S[UG0-9]/.test(intervention.ids?.[0]))
				intervention.id = intervention.ids[0] // take over remaining id
			else
				// no remaining senses, remove the whole intervention, no longer has any treatments left
				interventions.splice(i--, 1)
		}
	}
	if (!foundIntervention) {
		interventions.push({
			id: key,
			ids: [ key ],
			label,
		})
	}

	structuredQuery.set('interventions', interventions)
}
analyzeTreatmentAs.removeFromAnalysis = function(key) {
	let analyzeAsArray = currentAnalysisSelections.analyzeAs.valueOf()
	let existing = analyzeAsArray?.find(analyzeAsGroup => analyzeAsGroup.label == key)
	let existingMultiple = analyzeAsArray?.filter(analyzeAsGroup => analyzeAsGroup.label == key)
	if (existingMultiple) {
		existingMultiple.forEach(element => {
			currentAnalysisSelections.analyzeAs.delete(element)
		})
	}
	let intervention = structuredQuery.interventions.valueOf().find(intervention => intervention.label == key)
	if (intervention !== undefined) {
		structuredQuery.interventions.delete(intervention)
		currentAnalysisSelections.analyzeAs.delete(existing)
	}
	structuredQuery.put(structuredQuery.valueOf())
}
analyzeTreatmentAs.ungroup = function(groupName, term) {
	let analyzeAsArray = currentAnalysisSelections.analyzeAs.valueOf()
	let existingAnalyzeAs = analyzeAsArray?.find(analyzeAsGroup => analyzeAsGroup.label == groupName)
	if (existingAnalyzeAs)
		currentAnalysisSelections.analyzeAs.splice(analyzeAsArray.indexOf(existingAnalyzeAs), 1)
	if (!term)
		return
	let group = structuredQuery.interventions.valueOf().find(intervention => intervention.label == groupName && intervention.contains)
	if (group)
		structuredQuery.interventions.delete(group)
	let existingQueryTerm = structuredQuery.interventions.valueOf().find(intervention => intervention.label == term.label)
	if (term.contains)
		term.contains = null
	if (!existingQueryTerm)
		structuredQuery.interventions.add(term)
	structuredQuery.put(structuredQuery.valueOf())
}
let lastQuestion

export const isLiveEditing = reactive(false)
export let buildingQuestion

export const liveStructuredQuery = liveParsed.to(async parsedQuestion => {
	let structuredQuery = {
		outcomes: [],
		characteristics: [],
		interventions: [],
		demographics: {},
		textQuery: questionToText(parsedQuestion.annotations)
	}
	let isUnion
	parsedQuestion.annotations.forEach((phrase, i) => {
		if (typeof phrase == 'object') {
			let attributes = structuredQuery[phrase.type == 'outcome' ? 'outcomes' : phrase.type == 'intervention' ? 'interventions' : 'characteristics']
			if (!attributes)
				return
			let nextAttribute = Object.assign({}, phrase, {
				label: phrase.label || phrase.type,
				or: null,
			})
			if (nextAttribute.ids) {
				nextAttribute.ids.forEach(id => id.split('/').forEach(termId => {
					switch (termId) {
						case MALE_SENSE_ID: case 'male': structuredQuery.demographics.gender = 'male'
							return
						case FEMALE_SENSE_ID: case 'female': structuredQuery.demographics.gender = 'female'
							return
						case ELDERLY_SENSE_ID: case 'elderly': structuredQuery.demographics.age = 70
							return
						case YOUNG_SENSE_ID: case YOUTH_SENSE_ID: case 'young': case 'youth':
							structuredQuery.demographics.age = 16
							return
						case CHILD_SENSE_ID: case 'child':
							structuredQuery.demographics.age = 9
							return
						case INFANT_SENSE_ID: case 'infant':
							structuredQuery.demographics.age = 1
							return
					}
				}))
			}
			if (isUnion && attributes.length > 0 && phrase.type == 'condition') {
				let lastAttribute = attributes[attributes.length - 1]
				while (lastAttribute.or) {
					lastAttribute = lastAttribute.or
				}
				lastAttribute.or = nextAttribute
			} else {
				nextAttribute.anded = true
				attributes.push(nextAttribute)
			}
		} else {
			isUnion = true
			if (/^[\s,]*(or|and|patients with|)\s*$/i.test(phrase)) {
				for (let j = i; j < parsedQuestion.annotations.length; j++) {
					let conjunction, phrase = parsedQuestion.annotations[j]
					if (typeof phrase == 'string') {
						let parsed = phrase.match(/[\s,]*(or|and|patients with|)\s*/i)
						if (parsed) {
							if (parsed[1].toLowerCase() == 'and' || parsed[1].toLowerCase() == 'patients with') {
								isUnion = false
								break
							} else if (parsed[1].toLowerCase() == 'or')
								break
							// else continue searching for a conjunction
						} else
							break
					}
				}
			}
		}
	})
	return structuredQuery
}, (newStructuredQuery) => {
	// transform reversal
	liveStructuredQuery.then(oldStructuredQuery => {
		let filtersWritten = {
			outcomes: [],
			characteristics: [],
			interventions: [],
		}
		let annotationOffset = 0
		buildingQuestion = true
		let annotations = liveParsed.valueOf().annotations
		let lastPosition = 0

		let outcomes = newStructuredQuery.outcomes.map(outcome =>
			Object.assign({}, outcome, { type: 'outcome' }))
		let characteristics = newStructuredQuery.characteristics.map(characteristic =>
			Object.assign({}, characteristic, { type: 'condition' }))
		let interventionByLabel = new Map()
		let interventions = newStructuredQuery.interventions.map(intervention =>
			Object.assign({}, intervention, { type: 'intervention' }))

		let outcomeIds = outcomes.map(outcome => outcome.id)
		let characteristicIds = characteristics.map(characteristic => characteristic.id)
		let interventionIds = interventions.map(intervention => intervention.id)
		let i = 0
		let newQuestion = []
		for (let phrase of annotations) {
			let prefixLength
			let moreOfType = annotations.slice(i + 1).some(otherAnnotation => phrase.type == otherAnnotation.type)
			if (phrase.type && annotations[i - 1]?.type != phrase.type)
				prefixLength = 0
			let suffixLength
			if (phrase.type && annotations[i + 1]?.type == phrase.type)
				suffixLength = annotations[i + 1].start - lastPosition

			let newPhrase
			let attributes
			switch (phrase.type) {
				case 'outcome':
					attributes = outcomeIds
					break
				case 'characteristic': case 'condition':
					attributes = characteristicIds
					break
				case 'intervention':
					attributes = interventionIds
					break
			}
			if (!attributes) {
				newQuestion.push(phrase)
				continue
			}
			let index = attributes.indexOf(phrase.id)
			if (index > -1) {
				// found it, preserve it
				newPhrase = phrase
				// remove it now to indicate it has been written
				attributes.splice(index, 1)
			} else {
				// no longer needed, remove from new query
				newPhrase = ''
			}
			if (attributes.length > 0 && !moreOfType) {
				if (newPhrase)
					attributes.unshift(newPhrase)
				newPhrase = toQuestionPhrases(attributes, attributes == interventionIds ? 'or' : 'and')
				attributes.splice(0, attributes.length) // empty it, we have written them all
			}
			let newTextLength = newPhrase.length
			if (!newPhrase) {
				// if we are removing this annotation, potentially remove connecting text
				if (prefixLength) {
					newTextQuery = newTextQuery.slice(0, newTextQuery.length - prefixLength)
					newTextLength = - prefixLength
				} else if (suffixLength) {
					lastPosition += suffixLength
				}
				if (attributes == interventionIds && newStructuredQuery.interventions.length == 0) {
					phrase = 'any treatment'
				}
			}
			if (phrase.start > -1) {
				annotationOffset += newTextLength + phrase.start - phrase.end
				phrase.end = phrase.start + newTextLength
			}
			newQuestion.push(newPhrase)
			i++
		}

		if (interventions.length > 0 || outcomes.length > 0 || characteristics.length > 0) {
			// rewrite the sentence for now
			newQuestion = generateNewQuestion({ interventions, outcomes, characteristics }, { analysisType })
		}
		//newTextQuery += oldTextQuery.slice(lastPosition)
		//newTextQuery = newTextQuery.replace(/~/g, '\xA0')
		liveParsed.put({
			annotations: newQuestion
		})
		suggestions.put([])
		liveStructuredQuery.is(newStructuredQuery) // so we don't recompute the structured query
		updateLocationForSearch()
	})
})

type Phrase = {
	type: string
	list?: string[]
	conjunction?: string
	text?: string
	analyzeAs?: string
}

class ExecutedQuery extends Variable.with({
	interventions: [],
	outcomes: [],
	characteristics: [],
}) {
	get isWritable() {
		return true
	}
	put(newQuery) {
		liveStructuredQuery.put(newQuery)
		super.put(newQuery)
		if (!newQuery.isAsking)
			analysisSelections.question.put(liveParsed.valueOf().annotations)

		/*	if (!interventionsListed && interventions.length > 0) {
				// if no interventions, we rewrite the beginning of the query
				if (phrases[0].type === 'wh')
					phrases.splice(0, 1)
				phrases.push({
					type: 'wh',
					text: interventions.length == 1 ? 'How effective is' : 'Compare',
				}, {
					type: 'interventions',
					list: interventions,
					conjunction: 'vs'
				})
			}*/
	}
}

export const groupInterventions = interventions => {
	let interventionByLabel = new Map()
	for (let intervention of interventions) {
		if (typeof intervention == 'string') {
			intervention = { id: intervention }
		}
		let label = analyzeTreatmentMap.get(intervention.label) || intervention.label || intervention.id
		let interventionGroup = interventionByLabel.get(label)
		if (interventionGroup) {
			interventionGroup.id += ',' + intervention.id
			interventionGroup.ids.push(intervention.id)
			interventionGroup.contains.push(intervention.label)
		} else {
			interventionByLabel.set(label, Object.assign({}, intervention, {
				type: 'intervention',
				label,
				ids: [intervention.id],
				contains: [ intervention.label ],
			}))
		}
	}
	return Array.from(interventionByLabel.values())
}
export const structuredQuery = window.structuredQuery = new ExecutedQuery({
	characteristics: [],
	interventions: [],
	outcomes: [],
	undefined: [],
	isAsking: true,
})
export const cooccurrences = reactive({
	outcomes: [],
	interventions: [],
	characteristics: [],
})

export const onExecuteSearchActions = []
let cooccurrenceCache = new Map()
export let askingQuestion = true
export function executeSearch(initialSearch?) {
	if (askingQuestion) {
		askingQuestion = false
		updateLocationForSearch(initialSearch, !initialSearch)
	}
	return liveParsed.then(async ({ annotations, selectionsId, interventionalScore }) => {
		if (selectionsId) {
			loadSelections(selectionsId)
			updateAnalysis(true)
			updateLocationForSearch()
		} else {
			let existingQuestion = initialSearch ? true : await analysisSelections.question
			let substantiveChange = initialSearch ? false : existingQuestion && (existingQuestion.map(part => part.label || part.id || part).join('') !=
				annotations.map(part => part.label || part.id || part).join(''))
			if (substantiveChange) {
				isNewQuestion().then(isNewQuestion => {
					if (isNewQuestion) {
						replaceSelections(makeId(), Object.assign(getDefaultSelections(), {
							question: annotations
						}))
						if (interventionalScore > 0.7 && analysisType == 'cohort' && !initialSearch) {
							analysisType.put('auto')
						} else if (interventionalScore < 0.3 && analysisType != 'cohort' && !initialSearch) {
							analysisType.put('cohort')
						}
					} else {
						analysisSelections.question.put(annotations)
					}
					updateAnalysis(true)
					updateLocationForSearch()
				})
			} else {
				analysisSelections.question[existingQuestion ? 'is' : 'put'](annotations)
			}
		}
		for (let onExecuteAction of onExecuteSearchActions)
			onExecuteAction()
		structuredQuery.is(liveStructuredQuery.then(query => {
			for (let demographic in query.demographics) {
				demographics[demographic].put(query.demographics[demographic])
			}
			return query
		}))
		updateCooccurrences()
		/*if (!initialSearch)
			analysisSelections.question.put(annotations)*/
		cooccurrences.put({
			outcomes: [],
			interventions: [],
			characteristics: [],
		})
	})
}
export async function updateCooccurrences() {
	let { interventions, outcomes, characteristics, isAsking } = await structuredQuery
	lastQuestion = cloneDeep({ interventions, outcomes, characteristics })
	let bluntQuery = await getBluntQuery({ outcomes: [], interventions, characteristics })
	if (bluntQuery) {
		cooccurrenceCache.set(bluntQuery, (cooccurrenceCache.get(bluntQuery) ||
			get('/portal/cooccurrences?query=' + encodeURIComponent(bluntQuery))).then(cooccurrencesResponse => {
			cooccurrences.put(cooccurrencesResponse)
			return cooccurrencesResponse
		}))
	}
}

const COVID_SENSE_IDS = ['SUT2bd', 'SUT2bX', 'SUUufO', 'SUUwAm', 'SGM2F', 'SUUwA0', 'SUUwwM', 'SUUAR8']

export function hasCovid19(characteristics) {
	return characteristics.some(characteristic => {
		do {
			let label = characteristic.label.toLowerCase()
			if (label.includes('covid') || label.includes('coronavirus') || label.includes('cov2') || label.includes('cov-2'))
				return true
		} while (characteristic = characteristic.or)
	})
}

export async function getBluntQuery({ outcomes, interventions, characteristics, forArticles, preferRCT, allowCTG }) {
	if (studyFilter.importCollection.valueOf())
		return // no query if we are only looking at imports
	let analysis = await analysisSelections
	let dsQuery = analysis?.dsQuery?.query
	let intersectInterventions = analysisType == 'direct'
	const forAttribute = attribute => {
		let parts = []
		do {
			let label = dsEscapeText(attribute.label)
			let ids = attribute.ids || [attribute.id || attribute.label]
			ids = ids.filter(id => id.split('/').every(id => id[0] == 'S' || id[0] > -1))
			let hasSequences = ids.some(id => id.includes('/')) || !label
			let joined = hasSequences ? ids.map(id => {
				let ids = id.split('/')
				let joined = ids.map(id => id[0] == 'S' ? 'x(ppmshallowdescendants).' + id : ('f(' + id + ')')).join('.')
				return ids.length > 1 ? 'x(l).' + joined + '.x(r)' : joined
			}).join('.x(or).') :
				(ids.length == 0 ? 'f(' + label + ')' :
					('x(ppmshallowdescendants).' + (ids.length > 1 ? 'kg(' : 'k(')) + ids.join('.') + '.' + label + '.)')
			if (ids.length > 1 && hasSequences)
				joined = 'x(l).' + joined + '.x(r)'
			parts.push(joined)
		} while (attribute = attribute.or)
		if (parts.length > 1)
			return 'x(l).' + parts.join('.x(or).') + '.x(r)'
		return parts[0]
	}
	function unionInterventions(parts) {
		if (intersectInterventions && parts.length > 1 || parts.length == 0)
			return parts
		return [ 'x(l)', parts.join('.x(or).'), 'x(r)' ]
	}
	let parts = [
		...unionInterventions(interventions.map(forAttribute)),
		...characteristics.map(forAttribute)].filter(part => part)
	if (dsQuery)
		parts.unshift(dsQuery)
	let bluntQuery = parts.join('.')
	let study = studyFilter.valueOf() || {}
	if (study.published > 1970)
		bluntQuery += '.a(p(' + study.published + '-01-01.))'
	let sources
	let sourcesQuery
	if (study.sources?.length > 0) {
		sources = study.sources
		if (allowCTG) {
			if (sources.includes('eu')) {
				// replace EU with WHO (avoid duplicating)
				if (sources.includes('who-ictrp'))
					sources = sources.filter(source => source != 'eu') // just remove it
				else
					sources = sources.map(source => source == 'eu' ? 'who-ictrp' : source)
			}
		} else
			sources = sources.filter(source => source != 'clinicaltrials' && source != 'eu')
		if (sources.length == 0)
			return // no query at all for CTG/EU-CTR
	} else if (dsQuery) {
		sources = []
	} else {
		sources = ['medline', 'who-ictrp', 'medrxiv']
		if ((await app.user)?.LegacyRoles?.includes('Congress'))
			sources.push('doc-congress')
		if (hasCovid19(characteristics)) // only use it Covid-19 is a characteristic
			sources.push('cord19')
	}
	sources = sources.map(source => sourcePills[source] || ('.c(' + source + ')'))
	sourcesQuery = sources.length > 1 ? '.x(l)' + sources.join('.x(or)') + '.x(r)' :
		sources[0]

	let onlyPubmedPills
	if (study.designs?.length > 0) {
		if (sourcesQuery)
			bluntQuery += sourcesQuery + '.x(l)' + study.designs.map(design => getDesign(design)).join('.x(or)') + '.x(r)'
	} else if (!dsQuery) {
		bluntQuery += sourcesQuery + '.x(l)' + getDesign('CS') + '.x(or)' + getDesign('RWE') + '.x(r)'
	}
	if (!dsQuery)
		bluntQuery += '.x(not).g(pubtype/D016422.PubMed Letter).x(not).g(pubtype/D016421.PubMed Editorial).x(not).g(pubtype/D016420.PubMed Comment)'
	if (study.locations)
		bluntQuery += study.locations.map(location => '.' + location).join('')
	let outcomeParts = outcomes.map(forAttribute)
	if (outcomeParts.length > 0)
		bluntQuery += '.x(boost).' + outcomeParts.join('.')

	let demographics = analysis.demographics || {}
	if (demographics.gender)
		bluntQuery += '.x(boost).k(' + (demographics.gender == 'male' ? MALE_SENSE_ID : FEMALE_SENSE_ID) + ')'
	if (demographics.age) {
		if (demographics.age > 60)
			bluntQuery += '.x(boost).k(' + ELDERLY_SENSE_ID + ')'
		else if (demographics.age < 13)
			bluntQuery += '.x(boost).k(' + CHILD_SENSE_ID + ')'
		else if (demographics.age < 18)
			bluntQuery += '.x(boost).k(' + ADOLESCENCE_SENSE_ID + ')'
		else
			bluntQuery += '.x(boost).k(' + ADULT_SENSE_ID + ')'
	}
	if (demographics.race) {
		if (demographics.race == 'white')
			bluntQuery += '.x(boost).k(' + WHITE_SENSE_ID + ')'
		else if (demographics.race == 'black')
			bluntQuery += '.x(boost).k(' + AFRICAN_SENSE_ID + ')'
		else if (demographics.race == 'asian')
			bluntQuery += '.x(boost).k(' + ASIAN_SENSE_ID + ')'
		else if (demographics.race == 'pacific')
			bluntQuery += '.x(boost).k(' + PACIFIC_ISLANDER_SENSE_ID + ')'
		else if (demographics.race == 'native american')
			bluntQuery += '.x(boost).k(' + NATIVE_AMERICAN_SENSE_ID + ')'
		else if (demographics.race == 'multi-race')
			bluntQuery += '.x(boost).k(' + MIXED_RACE_SENSE_ID + ')'
	}
	
	if (preferRCT && !dsQuery && !(study.designs?.length > 0 && sourcesQuery)) {
		let predicted = '.x(boost)' + getDesign('RCT')
		bluntQuery += predicted
		// based on https://doctorevidence.slack.com/archives/G096X8Y9F/p1617040820062200 turn this off to try without pubmed pills:
		//onlyPubmedPills = true
		//bluntQuery += '.x(boost)' + getDesign('RCT') + predicted
	}
	return bluntQuery
	function getDesign(design) {
		let pill = designPills[design] || ''
		if (onlyPubmedPills) {
			pill = pill.replace(/predicted/, 'pubtype')
		}
		return pill
	}
}
const designPills = {
	RCT: '.g(predicted/D016449.Randomized Controlled Trial)',
	CS: '.g(predicted/D016430.Clinical Study)',
	RWE: '.g(predicted/rwe.Real-world Evidence)',
}
const sourcePills = {
	medrxiv: '.s(http://connect~.medrxiv~.org/medrxiv_xml~.php?subject=all)',
}


export const parameterToQuestion = parameter => {
	let question = []
	parameter.replace(/\(([^\)]+)\)|([^\(]+)/g, (t, typedPhrase, untypedPhrase) => {
		if (typedPhrase) {
			let [ type, ids, label ] = typedPhrase.split(':')
			if (!label) {
				label = ids
				ids = null
			} else {
				ids = ids.split(',')
			}
			question.push({ type, ids, label: decodeLabel(label) })
		} else
			question.push(decodeLabel(untypedPhrase))
		return ''
	})
	return question
}

let lastSearch
export let navigating
export function updateLocationForSearch(replace?, navigateTo?) {
	if (askingQuestion || navigating)
		return
	let question = liveParsed.annotations.valueOf()
	let freshSearch = location.search.slice(1).replace(/search=[^&#]*/, '').replace(/&selections=[^&#]*/, '').replace(/&query=[^&#]*/, '').replace(/&title=[^&#]*/, '')
	if (freshSearch && freshSearch.charAt(0) != '&')
		freshSearch = '&' + freshSearch
	let newSearch = '?search=' + questionToURIComponent(question) +
		'&selections=' + analysisSelections.id + freshSearch +
		(docSearchQuery.query.valueOf() ? '&query=' + docSearchQuery.query : '')

	if (newSearch != location.search) {
		if (navigateTo) {
			navigating = true
			return location.href = newSearch + location.hash
		}
		history[replace ? 'replaceState' : 'pushState']({}, questionToText(question), newSearch + location.hash)
	}
	document.title = 'DocAnalytics: ' + questionToText(question)
	lastSearch = location.search
}

export let whenInitialSearch
function updateLiveTextFromLocation(event?) {
	if (lastSearch == location.search)
		return // no change in search parameter, don't make any changes
	lastSearch = location.search
	if (!lastSearch && event)
		return location.reload()
	let searchParameter = location.search.match(/search=([^&]+)/)
	let importsParameter = location.search.match(/imports=([^&]+)/)
	let dsQueryParameter = location.search.match(/query=([^&]+)/)
	let dsTitleParameter = location.search.match(/title=([^&]+)/)
	if (importsParameter) {
		studyFilter.set('importCollection', decodeURIComponent(importsParameter[1]))
		if (!searchParameter) {
			return executeSearch(true)
		}
	}
	let selectionsParameter = location.search.match(/selections=([^&]*)/)
	if (selectionsParameter?.[1] && selectionsParameter[1] != analysisSelections.id) {
		let id = selectionsParameter[1]
		if (id == 'example') {
			analysisSelections.set('example', true)
			if (location.hash == '#direct')
				analysisSelections.set('analysisType', 'direct')
			else if (location.hash == '#network' && analysisSelections.analysisType != 'frequentist' && analysisSelections.analysisType != 'bayesian')
				analysisSelections.set('analysisType', 'auto')
			else if (analysisSelections.analysisType == 'cohort')
				analysisSelections.set('analysisType', 'auto')
		} else {
			loadSelections(selectionsParameter[1])
		}
		if (!searchParameter && !dsQueryParameter)
			return executeSearch(true)
	}
	let question
	if (searchParameter) {
		if (questionToURIComponent(liveParsed.annotations.valueOf()) == searchParameter[1])
			return // no change in the question
		question = parameterToQuestion(searchParameter[1])
		document.title = 'DocAnalytics: ' + questionToText(question)
		return whenInitialSearch = parseQuestion(question).then(({ annotations, interventionalScore }) => {
			liveParsed.put({
				annotations,
				selectionsId: null,
				interventionalScore,
			})
			suggestions.put([])
			if (dsQueryParameter) {
				return updateForDSQuery().then(() => executeSearch(true))
			}
			return executeSearch(true)
		})
	}
	if (dsQueryParameter) {
		updateForDSQuery()
		executeSearch(true)
	}
	if (question && !selectionsParameter) {
		let newSearch = location.search + '&selections=' + analysisSelections.id
		history.replaceState({}, question, newSearch)
	}
	function updateForDSQuery() {
		return Promise.resolve(analysisSelections).then(() => {
			let query = decodeURIComponent(dsQueryParameter[1])
			let name = dsTitleParameter ? decodeURIComponent(dsTitleParameter[1]) : query.match(/ss\([^\.]+\.([^)]+)\)/)?.[1]
			if (query != analysisSelections.dsQuery.query) {
				analysisSelections.dsQuery.put({
					query: decodeURIComponent(dsQueryParameter[1]),
					name: name || ('DS Query ' + new Date().toLocaleString()),
				})
			}
		})
	}
}

updateLiveTextFromLocation()
addEventListener('popstate', updateLiveTextFromLocation)

export const metaResults = new VArray()
Promise.resolve(app.user).then(user => {
	if (!location.hash.includes('view=report'))
		updateAnalysis(true)
})
/*
Promise.resolve(whenInitialSearch).then(() => structuredQuery.to(async (question) =>
	question.isAsking ? {} : setTime
	get('/portal/analysis/meta?' + await questionAsParameters()))))*/

export function makeId() {
	// produces a 16 digit alpha id
	let numbers = new Uint8Array(12)
	return btoa(String.fromCharCode(...crypto.getRandomValues(numbers))).replace(/[/+]/g,'-')
}

function mapFromArray(variable) {
	return variable.to(array => {
		let map = new Map()
		for (let { key, value } of array)
			map.set(key, value)
		return map
	}, map =>
		variable.put(Array.from(map).map(([key, value]) => ({ key, value })))
	).as(VMap)
}
function isNewQuestion() {
	return liveStructuredQuery.then(question => {
		if (!lastQuestion) {
			lastQuestion = cloneDeep(question)
			return false
		}
		let attributes = toAttributes(question)
		let lastAttributes = toAttributes(lastQuestion)
		lastQuestion = cloneDeep(question)
		return intersection(attributes, lastAttributes).length < Math.sqrt(attributes.length * lastAttributes.length) / 2
	})
}
function toAttributes(query) {
	let { outcomes, interventions, characteristics } = query
	let attributes = []
	for (let { id } of [...outcomes, ...interventions, ...characteristics]) {
		let rootId = id ? id.split(',')[0] : ''
		attributes.push(rootId.split('/')[0])
	}
	return attributes
}
export const encodeAttributeAsValue = terms => terms.map(term => {
	let ids = []
	let parts = []
	do {
		let label = term.label
		let ids = term.ids || [term.id || term.label.toLowerCase()]
		parts.push((typeof label == 'string' ? encodeBluntLabel(label) + ':' : '') + ids.join(','))
	} while (term = term.or)
	return parts.join(',')
}).join('.')

export function questionAsParameters() {
	return structuredQuery.then(async ({ outcomes, interventions, characteristics }) => {
		const encodePart = terms => terms.map(term => {
			let ids = []
			let parts = []
			do {
				let label = term.label
				let ids = term.ids || [term.id || term.label.toLowerCase()]
				parts.push((typeof label == 'string' ? encodeBluntLabel(label) + ':' : '') + ids.join(','))
			} while (term = term.or)
			return parts.join(',')
		}).join('.')

		let interventionSets = new Cache()
		for (let intervention of interventions) {
			let analyzeAs = analyzeTreatmentAs.get(intervention.id) || intervention.label
			interventionSets.get(analyzeAs, () => []).push(...(intervention.ids || [ intervention.id ]))
		}
		let interventionsEncoded = Array.from(interventionSets.map).map(([ name, ids ]) =>
			(typeof name == 'string' ? encodeBluntLabel(name) + ':' : '') + ids.join(',')).join('.')
		currentQuestion.put({
			outcomes: encodeAttributeAsValue(outcomes),
			interventions: interventionsEncoded,
			characteristics: encodeAttributeAsValue(characteristics),
		})

		let search = 'outcomes=' +
		encodeAttributeAsValue(outcomes) +
				'&interventions=' +
				interventionsEncoded +
				'&characteristics=' +
				encodeAttributeAsValue(characteristics) +
				'&question=' +
				questionToURIComponent(liveParsed.annotations.valueOf()) +
				'&analysis=' +
				analysisSelections.id +
				 '&analysisType=' +
				analysisType
		let { age, gender, race } = (await demographics) || {}
		let { designs, locations, published, sources, importCollection } = (await studyFilter) || {}

		if (importCollection) {
			search += '&imports=' + encodeURIComponent(importCollection)
		}
		if (sources) {
			search += '&sources=' + sources
		}
		if (designs?.length > 0) {
			search += '&designs=' + designs
		}
		if (locations?.length > 0) {
			search += '&locations=' + locations
		}
		if (published > 1970) {
			search += '&published=' + published
		}
		if (age) {
			search += '&age=' + age
		}
		if (race) {
			search += '&race=' + race
		}
		if (gender) {
			search += '&gender=' + gender
		}
		let currentStudySelections = await studySelections
		let currentSelected = await selectedStudies;
		if (currentStudySelections) {
			let requiredStudyIds = []
			let removeGroupingIds = []
			let addGroupingIds = []
			if (window.isFAQDashboard)
				requiredStudyIds = currentSelected
			else {
				for (let studyId in currentStudySelections) {
					if (!currentSelected.includes(studyId))
						continue
					let selections = currentStudySelections[studyId]
					if (selections.outcomeId || selections.included) // use this as a marker for a "frozen" study selection
						requiredStudyIds.push(studyId)
					
					let studySettings = studySelections.property(studyId)
					if (studySettings.property('groupRemove') && studySettings.get('groupRemove') == true && !removeGroupingIds.includes(studyId)) {
						removeGroupingIds.push(studyId)
					}
					if (studySettings.property('groupAdd') && studySettings.get('groupAdd') !== undefined) {
						// don't add dupes
						let groupAddSetting = studySettings.get('groupAdd')
						let sourceId = groupAddSetting.source
						let targetId = groupAddSetting.target
						let match = addGroupingIds.find(pair => pair.target == targetId && pair.source == sourceId)
						if (match) continue
						else addGroupingIds.push(groupAddSetting)	
					}			
				}
			}
			if (requiredStudyIds.length > 0)
				search += '&studyIds=' + requiredStudyIds.join(',')
			if (removeGroupingIds.length > 0)
				search += '&groupRemove=' + removeGroupingIds.join(',')
			if (addGroupingIds.length > 0) {
				const pairs = []
				for (let pair of addGroupingIds) {
					pairs.push(Object.values(pair).join('/'))
				}
				search += '&groupAdd=' + pairs.join(',')
			}
		}
		if (!outcomes?.[0] || outcomeSettings.settingsFor == outcomes[0].label)
			search += '&unit=' + encodeURIComponent(outcomeSettings.unit)
		if ((await analysisSelections.settings.multiGroupPooling) === false)
			search += '&group-pooling=false'
		if (analysisSelections.settings.get('additiveInterventions'))
			search += '&additive-interventions=true'
		if (analysisSelections.dsQuery.query.valueOf())
			search += '&dsQuery=' + analysisSelections.dsQuery.query
		return search
	})
}

export const readonlyMode = reactive(app.user).to(user => !user?.FeatureConfig?.['Build Analyses']?.Enabled)

export const readonlyValueOrFaq = readonlyMode.valueOf() || isFAQDashboard

export const faqOrReadOnly = readonlyMode.to(readonlyMode => readonlyMode || isFAQDashboard)

export const faqMode = isFAQDashboard

export const adminFAQUser = reactive(app.user).to(user => user.Permissions.FAQCategory.Entity.Edit && !location.search.includes('admin=false'))

export const isEntryPage = location.search.match(/search|imports|selections/) == null
